/**
 * SSO Integrations — Frontend ローカル Contract
 *
 * BE 側 `dresscode-backend/v2/src/it-force/api/software-sso/usecase/sso-integrations-list.api-contract.ts`
 * と同じ Zod 構造を再現。BE が `@standandforce/api-contract` に publish された後、
 * client.ts でローカル import を置き換える想定。
 *
 * `paginationQuerySchema` / `paginatedSchema` は BE 共通スキーマ
 * （`shared-kernel/extension/orpc/contract/pagination.api-contract.ts`）を再現している。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';

/** SSO 連携の並び替え対象フィールド（現状は name のみ）。 */
const SsoIntegrationOrderFieldEnum = z.enum(['name']);

/** `search` は OAuth2 Client 名（clientName）の部分一致（大文字小文字無視）。 */
export const ListSsoIntegrationsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(2000).default(20),
  orderBy: SsoIntegrationOrderFieldEnum.optional(),
  orderDirection: z.enum(['asc', 'desc']).default('asc'),
  search: z.string().optional(),
});

export const SsoIntegrationItemSchema = z.object({
  softwareId: z.string(),
  name: z.string(),
  /** OAuth2 Client 名（Hydra client_name の非正規化コピー）。 */
  clientName: z.string().nullable(),
  /** SP-initiated ログイン URL（上書き → テンプレ×tenantDomain）。null なら FE は serviceUrl へ。 */
  loginUrl: z.string().nullable(),
  logoUrl: z.string().nullable(),
  /** servicePageUrl ?? library.websiteUrl。 */
  serviceUrl: z.string().nullable(),
});

export const ListSsoIntegrationsOutputSchema = z.object({
  items: z.array(SsoIntegrationItemSchema),
  length: z.number(),
  count: z.number(),
  page: z.number(),
  maxPage: z.number(),
});

export const ssoIntegrationsContract = oc.router({
  list: oc
    .route({ method: 'GET', path: '/v2/sso/integrations' })
    .input(ListSsoIntegrationsQuerySchema)
    .output(ListSsoIntegrationsOutputSchema),
});

export type ListSsoIntegrationsQuery = z.infer<
  typeof ListSsoIntegrationsQuerySchema
>;
export type SsoIntegrationItem = z.infer<typeof SsoIntegrationItemSchema>;
export type ListSsoIntegrationsOutput = z.infer<
  typeof ListSsoIntegrationsOutputSchema
>;
